const SWITCHER_SELECTOR = '[data-goodlot-collection-switcher]';
const SWITCHER_ROW_SELECTOR = '[data-goodlot-switcher-row]';
const MOBILE_QUERY = '(max-width: 749px)';
const MOBILE_REVEAL_INSET = 8;

function initGoodLotSwitcherRow(row) {
  if (!row || row.dataset.goodlotSwitcherRowInitialized === 'true') return;

  const track = row.querySelector('[data-goodlot-collection-switcher-track]');
  const dots = row.querySelector('[data-goodlot-collection-switcher-dots]');

  if (!track || !dots) return;

  row.dataset.goodlotSwitcherRowInitialized = 'true';

  let rafId = null;

  const isMobile = () => window.matchMedia(MOBILE_QUERY).matches;

  const isCategoryRow = row.classList.contains(
    'goodlot-collection-switcher__row--categories'
  );

  const categorySection = isCategoryRow
    ? row.closest('.goodlot-collection-switcher__category-section')
    : null;

  const categoryPrev = categorySection?.querySelector(
    '[data-goodlot-category-prev]'
  );

  const categoryNext = categorySection?.querySelector(
    '[data-goodlot-category-next]'
  );

  const categoryArrows = categorySection?.querySelector(
    '[data-goodlot-category-arrows]'
  );

  const categoryFade = row.querySelector(
    '[data-goodlot-category-fade]'
  );

  const getMaxScroll = () =>
    Math.max(0, track.scrollWidth - track.clientWidth);

  /* Existing mobile dots */
  const getPageCount = () => {
    if (!isMobile()) return 0;
    if (track.scrollWidth <= track.clientWidth + 2) return 1;

    return Math.max(
      2,
      Math.min(
        6,
        Math.ceil(track.scrollWidth / Math.max(track.clientWidth, 1))
      )
    );
  };

  const buildDots = () => {
    dots.replaceChildren();

    const count = getPageCount();
    if (!count) return;

    for (let index = 0; index < count; index += 1) {
      const dot = document.createElement('span');
      dot.className =
        `goodlot-collection-switcher__dot${index === 0 ? ' is-active' : ''}`;
      dots.appendChild(dot);
    }
  };

  const updateDots = () => {
    const dotItems = [...dots.children];
    if (!dotItems.length) return;

    const maxScroll = getMaxScroll();
    const progress = maxScroll === 0 ? 0 : track.scrollLeft / maxScroll;
    const activeIndex = Math.round(progress * (dotItems.length - 1));

    dotItems.forEach((dot, index) => {
      dot.classList.toggle('is-active', index === activeIndex);
    });
  };

  /* Collection page always begins from the left — never center it. */
  const updateCategoryAlignment = () => {
    if (!isCategoryRow) return;
    track.classList.remove('is-centered');
  };

  const updateCategoryControls = () => {
    if (!isCategoryRow) return;

    if (isMobile()) {
      if (categoryArrows) categoryArrows.hidden = true;

      if (categoryFade) {
        const maxScroll = getMaxScroll();
        categoryFade.style.display =
          maxScroll > 2 && track.scrollLeft < maxScroll - 2
            ? 'block'
            : 'none';
      }

      return;
    }

    const maxScroll = getMaxScroll();
    const current = track.scrollLeft;
    const hasOverflow = maxScroll > 2;

    if (categoryPrev) {
      categoryPrev.disabled = !hasOverflow || current <= 2;
    }

    if (categoryNext) {
      categoryNext.disabled =
        !hasOverflow || current >= maxScroll - 2;
    }

    if (categoryArrows) {
      categoryArrows.hidden = !hasOverflow;
    }

    if (categoryFade) {
      categoryFade.style.display =
        hasOverflow && current < maxScroll - 2
          ? 'block'
          : 'none';
    }
  };

  const getCategoryScrollDistance = () => {
    const firstCard = track.querySelector('[data-goodlot-category-card]');

    if (!firstCard) return track.clientWidth * 0.75;

    const styles = window.getComputedStyle(track);
    const gap =
      Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;

    return (firstCard.offsetWidth + gap) * 5;
  };

  const scrollCategories = (direction) => {
    if (!isCategoryRow || isMobile()) return;

    track.scrollBy({
      left: getCategoryScrollDistance() * direction,
      behavior: 'smooth',
    });
  };

  /*
   * MINIMUM REVEAL
   * If the tapped card is already fully visible, don't move.
   * If partially clipped, move only enough to reveal it completely.
   * Never center the card.
   */
  const getRevealTarget = (card) => {
    if (!card || !isMobile()) return track.scrollLeft;

    const trackRect = track.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();

    const cardLeft =
      track.scrollLeft + (cardRect.left - trackRect.left);

    const cardRight = cardLeft + cardRect.width;

    const visibleLeft =
      track.scrollLeft + MOBILE_REVEAL_INSET;

    const visibleRight =
      track.scrollLeft +
      track.clientWidth -
      MOBILE_REVEAL_INSET;

    let target = track.scrollLeft;

    if (cardRight > visibleRight) {
      target =
        cardRight -
        track.clientWidth +
        MOBILE_REVEAL_INSET;
    } else if (cardLeft < visibleLeft) {
      target =
        cardLeft - MOBILE_REVEAL_INSET;
    }

    return Math.max(0, Math.min(getMaxScroll(), target));
  };

  const revealCategoryCard = (card) => {
    if (!card || !isMobile()) return;

    const target = getRevealTarget(card);

    if (Math.abs(track.scrollLeft - target) <= 1) return;

    track.scrollTo({
      left: target,
      behavior: 'smooth',
    });
  };

  const setImmediateActiveCard = (card) => {
    if (!card) return;

    track
      .querySelectorAll('.goodlot-collection-switcher__category-card')
      .forEach((item) => {
        const active = item === card;

        item.classList.toggle('is-active', active);

        if (active) {
          item.setAttribute('aria-current', 'page');
        } else {
          item.removeAttribute('aria-current');
        }
      });
  };

  const handleCategoryClick = (event) => {
    if (!isCategoryRow) return;

    const card = event.target.closest(
      '.goodlot-collection-switcher__category-card'
    );

    if (!card || !track.contains(card)) return;

    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    /* Existing active-category toggle back to All deals */
    if (card.classList.contains('is-active')) {
      const switcher = row.closest(SWITCHER_SELECTOR);

      const allDealsPill =
        switcher?.querySelector(
          '.goodlot-collection-switcher__row--deals a[href*="/collections/this-weeks-drops"]'
        ) ||
        switcher?.querySelector(
          '.goodlot-collection-switcher__row--deals a[href*="/collections/all-deals"]'
        ) ||
        switcher?.querySelector(
          '.goodlot-collection-switcher__row--deals .goodlot-collection-switcher__pill'
        );

      if (!allDealsPill) return;

      event.preventDefault();
      event.stopPropagation();

      allDealsPill.click();
      return;
    }

    /*
     * Important:
     * exactly ONE carousel movement happens here.
     * AJAX no longer destroys or reinserts the selector, so nothing
     * will reset scrollLeft and then animate back afterward.
     */
    revealCategoryCard(card);
    setImmediateActiveCard(card);
  };

  const onScroll = () => {
    if (rafId !== null) return;

    rafId = requestAnimationFrame(() => {
      rafId = null;
      updateDots();
      updateCategoryControls();
    });
  };

  const refresh = () => {
    updateCategoryAlignment();
    buildDots();

    requestAnimationFrame(() => {
      updateDots();
      updateCategoryControls();
    });
  };

  track.addEventListener('scroll', onScroll, { passive: true });

  if (isCategoryRow) {
    track.addEventListener('click', handleCategoryClick);

    categoryPrev?.addEventListener('click', () => {
      scrollCategories(-1);
    });

    categoryNext?.addEventListener('click', () => {
      scrollCategories(1);
    });
  }

  /*
   * Dynamic collection updates now keep this exact selector DOM alive.
   * Only refresh dots/control state — NEVER change its scroll position here.
   */
  document.addEventListener('goodlot:collection-dynamic-updated', () => {
    if (!document.documentElement.contains(row)) return;

    requestAnimationFrame(() => {
      updateDots();
      updateCategoryControls();
    });
  });

  window.addEventListener('resize', refresh, { passive: true });

  refresh();
}

function initGoodLotCollectionSwitcher(root) {
  if (!root) return;

  root.querySelectorAll(SWITCHER_ROW_SELECTOR).forEach((row) => {
    initGoodLotSwitcherRow(row);
  });
}

function initAllGoodLotCollectionSwitchers(scope = document) {
  scope.querySelectorAll(SWITCHER_SELECTOR).forEach((root) => {
    initGoodLotCollectionSwitcher(root);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    () => initAllGoodLotCollectionSwitchers(document),
    { once: true }
  );
} else {
  initAllGoodLotCollectionSwitchers(document);
}

document.addEventListener('goodlot:collection-dynamic-updated', () => {
  initAllGoodLotCollectionSwitchers(document);
});

document.addEventListener('shopify:section:load', (event) => {
  initAllGoodLotCollectionSwitchers(event.target);
});

/* ==========================================================
   GOODLOT COLLECTION — TODAY'S DROP COUNTDOWN
   Uses Pacific Time and survives AJAX collection switching.
   ========================================================== */

function updateGoodLotCollectionDropCountdown() {
  const roots = document.querySelectorAll('[data-goodlot-collection-drop]');
  if (!roots.length) return;

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const parts = Object.fromEntries(
    formatter
      .formatToParts(new Date())
      .map((part) => [part.type, part.value])
  );

  const currentSeconds =
    Number(parts.hour) * 3600 +
    Number(parts.minute) * 60 +
    Number(parts.second);

  const dropSeconds = 10 * 3600;
  const beforeDrop = currentSeconds < dropSeconds;

  const seconds = beforeDrop
    ? dropSeconds - currentSeconds
    : 24 * 3600 - currentSeconds + dropSeconds;

  const day = beforeDrop ? 'Today' : 'Tomorrow';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;

  const timeText =
    `${String(hours).padStart(2, '0')}:` +
    `${String(minutes).padStart(2, '0')}:` +
    `${String(remainingSeconds).padStart(2, '0')}`;

  roots.forEach((root) => {
    const timeNode = root.querySelector(
      '[data-goodlot-collection-drop-time]'
    );

    const dayNode = root.querySelector(
      '[data-goodlot-collection-drop-day]'
    );

    if (timeNode) timeNode.textContent = timeText;
    if (dayNode) dayNode.textContent = day;
  });
}

updateGoodLotCollectionDropCountdown();

if (!window.__goodlotCollectionDropCountdownInterval) {
  window.__goodlotCollectionDropCountdownInterval = window.setInterval(
    updateGoodLotCollectionDropCountdown,
    1000
  );
}

document.addEventListener(
  'goodlot:collection-dynamic-updated',
  updateGoodLotCollectionDropCountdown
);

document.addEventListener(
  'shopify:section:load',
  updateGoodLotCollectionDropCountdown
);
